import { RESOURCE_TYPE_LABEL_KEY, RESOURCE_TYPES } from '@/features/common/resourceType';
import { externalCatalogueHref } from '@/features/common/useExternalCatalogues';
import type { ExternalCatalogue } from '@/features/common/types/ExternalCatalogue';
import type { ResourceType } from '@/features/common/types/ResourceType';
import type { PublicRegistryKey } from '@/i18n/dictionaries/publicRegistry';
import type { LibraryResource } from '@/features/publicsite/types/registry/LibraryResource';
import type { LinkedCatalogue } from '@/features/publicsite/types/registry/LinkedCatalogue';

/** How many resources one page of /biblioteca lists. */
export const LIBRARY_PAGE_SIZE = 12;

/**
 * Whether anything in the URL narrows the listing — every parameter but the page counts.
 *
 * A search that matched nothing is not an empty library, and the two must not read the same:
 * "no records yet" sends the reader looking for a fault when the answer is to widen the filter.
 */
export function isLibraryFiltered(params: URLSearchParams): boolean {
  return [...params.keys()].some((k) => k !== 'page' && params.get(k)?.trim());
}

/**
 * What an empty grid says, or undefined for the shared "no records yet" line.
 *
 * Filtered to the international catalogues, this library's own rows are not the answer to what
 * was asked, so the grid is empty by design and says so; a filter that matched nothing says that.
 */
export function libraryEmptyKey(
  showNational: boolean,
  filtering: boolean,
): PublicRegistryKey | undefined {
  if (!showNational) return 'libraryNationalHidden';
  return filtering ? 'librarySearchEmpty' : undefined;
}

/** Whether a type code from the server is one the library names — it arrives as a string. */
function isResourceType(code: string): code is ResourceType {
  return RESOURCE_TYPES.some((type) => type === code);
}

/**
 * A resource card's badge key, or null for a type the backend has and this bundle does not name
 * yet — the card then prints `libraryType<CODE>`, which is what looking that unknown key up in the
 * dictionary has always printed.
 */
export function libraryTypeLabelKey(code: string): PublicRegistryKey | null {
  return isResourceType(code) ? RESOURCE_TYPE_LABEL_KEY[code] : null;
}

/** A resource card's muted line: authors, institution and year, whichever are known. */
export function resourceSubtitle(
  r: Pick<LibraryResource, 'authors' | 'institution' | 'year'>,
): string | null {
  return [r.authors?.join(', '), r.institution, r.year].filter(Boolean).join(' · ') || null;
}

/**
 * The international catalogues the page may link to for a query.
 *
 * Only the ones that resolved to an address this page may link to. A card for a catalogue the
 * visitor cannot open is worse than no card, and the section as a whole goes when none survive
 * rather than leaving a heading over an empty grid.
 */
export function linkableCatalogues(
  catalogues: ExternalCatalogue[],
  query: string,
): LinkedCatalogue[] {
  return catalogues
    .map((c) => ({ catalogue: c, href: externalCatalogueHref(c, query) }))
    .filter((c): c is LinkedCatalogue => c.href !== null);
}
